/**
 * EmailHealthPill — glanceable green/amber/red indicator of today's Resend
 * usage, driven by GET /api/admin/email-health. Polls every 60s. Clicking
 * the pill expands a small tooltip with the raw count/cap/pct/state.
 *
 * Green  = state === "ok"       (< 80% of daily cap)
 * Amber  = state === "warning"  (80-94%)
 * Red    = state === "critical" (>= 95%)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 60_000;

const STATE_STYLES = {
  ok: {
    dot: "bg-emerald-400",
    ring: "ring-emerald-400/30",
    text: "text-emerald-300",
    label: "OK",
  },
  warning: {
    dot: "bg-amber-400",
    ring: "ring-amber-400/40",
    text: "text-amber-300",
    label: "WARN",
  },
  critical: {
    dot: "bg-red-500",
    ring: "ring-red-500/40",
    text: "text-red-300",
    label: "CRIT",
  },
};

export const EmailHealthPill = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchHealth = async () => {
    try {
      const r = await axios.get(`${API}/admin/email-health`, { withCredentials: true });
      setData(r.data);
    } catch {
      // Non-admin users may hit this component; silent-fail is fine.
    }
  };

  useEffect(() => {
    fetchHealth();
    const t = setInterval(fetchHealth, POLL_MS);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const style = STATE_STYLES[data.state] || STATE_STYLES.ok;

  return (
    <div className="relative" data-testid="email-health-pill-wrapper">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/60 ring-1 ${style.ring} hover:bg-slate-800/70 transition-colors`}
        data-testid="email-health-pill"
        title="Today's email quota usage — click for details"
      >
        <span className={`w-2 h-2 rounded-full ${style.dot}`} data-testid="email-health-pill-dot" />
        <Mail className={`w-3.5 h-3.5 ${style.text}`} />
        <span className={`text-xs font-rajdhani font-bold ${style.text}`}>
          {data.count}/{data.cap}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 p-3 rounded-lg bg-slate-900 ring-1 ring-slate-700 shadow-xl z-50 text-xs text-slate-300"
          data-testid="email-health-pill-tooltip"
        >
          <div className="font-rajdhani font-bold text-white mb-2">Email quota — today ({data.date})</div>
          <div className="flex justify-between"><span>Sent:</span><span className={style.text + " font-bold"}>{data.count}</span></div>
          <div className="flex justify-between"><span>Daily cap:</span><span>{data.cap}</span></div>
          <div className="flex justify-between"><span>Used:</span><span className={style.text + " font-bold"}>{data.pct}%</span></div>
          <div className="flex justify-between"><span>Status:</span><span className={style.text + " font-bold uppercase"}>{style.label}</span></div>
          <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400">
            Warn at {data.warn_pct}% · Critical at {data.error_pct}%
          </div>
          {data.state !== "ok" && (
            <div className="mt-2 pt-2 border-t border-slate-700 text-amber-300 leading-relaxed">
              Approaching daily Resend cap. Failed sends auto-retry the next day.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailHealthPill;
